/**
 * Footer newsletter (HubSpot embedded form) — lazy loader.
 *
 * The footer renders two `.livom-hubspot-newsletter` placeholders (a desktop tree and a
 * mobile tree; one is display:none per breakpoint). They used to carry an inline script
 * each that injected js-eu1.hsforms.net/forms/embed/v2.js into <head> while the page was
 * still being parsed and built BOTH forms: ~1 MB, 13-16 requests and 9 web fonts on every
 * page view, for a form at the very bottom of the page (perf audit, Oct 2026).
 *
 * Same form, same portal, same form ids and the same placeholder/button overrides — only
 * the timing changes: nothing HubSpot-related is requested until a VISIBLE placeholder is
 * within ~800px of the viewport. The hidden twin never loads (display:none never
 * intersects) unless a resize makes it visible.
 *
 * Loaded as a module from the `code` setting of the two kc-custom-code blocks in
 * sections/footer-group.json, so it is evaluated once per page even though the tag is
 * emitted twice. The form styles (assets/kc-hubspot-newsletter.css) are requested together
 * with the embed, and the form is only created once they have loaded (no unstyled flash).
 */

const PORTAL_ID = '147363981';
const REGION = 'eu1';
const EMBED_SRC = 'https://js-eu1.hsforms.net/forms/embed/v2.js';
const SELECTOR = '.livom-hubspot-newsletter';

// One HubSpot form per storefront language. A store only ever resolves the languages it
// publishes (DE/AT: de + en, CH: de/en/fr/it, NL: nl + en).
const FORMS = {
  de: { formId: '25130a53-92a7-41f2-818b-f48ef3273323', placeholder: 'E-Mail-Adresse', button: 'Abonnieren' },
  en: { formId: 'ca3e064b-3c32-41f4-af56-e21d82b7bf75', placeholder: 'Email address', button: 'Subscribe' },
  fr: { formId: 'e8eda2c0-1bd6-426d-8ad5-236f3ec7b4df', placeholder: 'Adresse e-mail', button: 'S’inscrire' },
  it: { formId: 'a8ca709c-847d-438e-aa71-0c56052f866d', placeholder: 'Indirizzo e-mail', button: 'Iscriviti' },
  nl: { formId: 'cb87852e-a093-4f23-89cf-5fde6b89cc3d', placeholder: 'E-mailadres', button: 'Abonneren' },
};

function resolveLanguage(container) {
  // Same rule the inline version used: a locale prefix in the path wins...
  const path = window.location.pathname.replace(/\/+/g, '/').replace(/\/+$/, '');
  const prefix = (path.split('/')[1] || '').toLowerCase();
  if (prefix.length === 2 && FORMS[prefix]) return prefix;

  // ...otherwise the storefront's own language (rendered by Liquid on the placeholder).
  const fromLiquid = (container.dataset.lang || '').slice(0, 2).toLowerCase();
  if (FORMS[fromLiquid]) return fromLiquid;

  const fromDocument = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
  return FORMS[fromDocument] ? fromDocument : 'de';
}

let embedPromise = null;
function loadEmbed() {
  if (embedPromise) return embedPromise;

  embedPromise = new Promise((resolve, reject) => {
    const ready = () => window.hbspt && window.hbspt.forms && typeof window.hbspt.forms.create === 'function';
    if (ready()) return resolve();

    let script = document.querySelector('script[src*="js-eu1.hsforms.net/forms/embed/v2.js"]');
    if (!script) {
      script = document.createElement('script');
      script.charset = 'utf-8';
      script.src = EMBED_SRC;
      document.head.appendChild(script);
    }

    // The tag may have been inserted by someone else and already be loading/loaded, so
    // poll as well as listen (10 s ceiling, like the inline version).
    let attempts = 0;
    const timer = window.setInterval(() => {
      if (ready()) {
        window.clearInterval(timer);
        resolve();
      } else if (++attempts > 100) {
        window.clearInterval(timer);
        reject(new Error('HubSpot forms embed did not load'));
      }
    }, 100);
    script.addEventListener('error', () => {
      window.clearInterval(timer);
      reject(new Error('HubSpot forms embed failed to load'));
    });
  });

  // Let a later placeholder (or a later visit to the footer) retry after a failure.
  embedPromise.catch(() => {
    embedPromise = null;
  });

  return embedPromise;
}

let stylesPromise = null;
function loadStyles(href) {
  if (stylesPromise) return stylesPromise;
  if (!href) return (stylesPromise = Promise.resolve());

  stylesPromise = new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    // Never block the form on the stylesheet: resolve on load, on error, or after 3 s.
    link.addEventListener('load', resolve, { once: true });
    link.addEventListener('error', resolve, { once: true });
    window.setTimeout(resolve, 3000);
    document.head.appendChild(link);
  });

  return stylesPromise;
}

let sequence = 0;
function createForm(container) {
  if (container.hasAttribute('data-hubspot-loaded')) return;
  container.setAttribute('data-hubspot-loaded', 'true');

  const config = FORMS[resolveLanguage(container)];

  Promise.all([loadEmbed(), loadStyles(container.dataset.css)])
    .then(() => {
      if (!container.isConnected) return;

      const uniqueId = `livom-hubspot-newsletter-${Date.now()}-${sequence++}-${Math.random().toString(36).substring(2, 8)}`;
      container.id = uniqueId;

      window.hbspt.forms.create({
        portalId: PORTAL_ID,
        formId: config.formId,
        region: REGION,
        target: `#${uniqueId}`,
        css: '',

        onFormReady(hubspotForm) {
          let form;
          if (hubspotForm && hubspotForm.nodeType === 1) {
            form = hubspotForm;
          } else if (hubspotForm && hubspotForm[0]) {
            form = hubspotForm[0];
          } else {
            form = container.querySelector('form');
          }
          if (!form) return;

          const emailInput = form.querySelector('input[type="email"], input[name="email"]');
          if (emailInput) {
            emailInput.placeholder = config.placeholder;
            emailInput.setAttribute('aria-label', config.placeholder);
          }

          const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
          if (submitButton) {
            if (submitButton.tagName === 'INPUT') {
              submitButton.value = config.button;
            } else {
              submitButton.textContent = config.button;
            }
            submitButton.setAttribute('aria-label', config.button);
          }
        },
      });
    })
    .catch(() => {
      // Embed blocked or offline: allow another attempt the next time it comes into view.
      container.removeAttribute('data-hubspot-loaded');
    });
}

let observer = null;
function watch(container) {
  if (container.__kcNewsletterWatched) return;
  container.__kcNewsletterWatched = true;

  if (!('IntersectionObserver' in window)) {
    // Very old browsers: fall back to the previous behaviour, but after `load`.
    const start = () => createForm(container);
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return;
  }

  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          createForm(entry.target);
          // Keep observing only if the attempt was rolled back (failure) — a created
          // form never needs the observer again.
          if (entry.target.hasAttribute('data-hubspot-loaded')) observer.unobserve(entry.target);
        });
      },
      { rootMargin: '800px 0px' }
    );
  }

  observer.observe(container);
}

function init(scope = document) {
  scope.querySelectorAll(SELECTOR).forEach(watch);
}

init();
// Theme editor: the footer section can be re-rendered with fresh placeholders.
document.addEventListener('shopify:section:load', (event) => init(event.target));
